import {type InfiniteData, useInfiniteQuery} from "@tanstack/react-query";
import {queryHttp} from "../../../query/queryHttp";
import type {PageResponse} from "../../../services/ApiTypes";

/** One parent supplier group from `GET …/data/{bag_id}/supplier_parent_supplier/`. */
export type InsightSupplierRow = {
    sp_name: string;
    suppliers: string[];
};

export type InsightSupplierListResponse = PageResponse<InsightSupplierRow>;

export const insightSupplierListQueryKey = (bagId: number, search: string) =>
    ["insights", "supplier_parent_supplier", bagId, search] as const;

export function useInsightSupplierListQuery(
    bagId: number | undefined,
    debouncedSearch: string,
) {
    return useInfiniteQuery<
        InsightSupplierListResponse,
        Error,
        InfiniteData<InsightSupplierListResponse>,
        ReturnType<typeof insightSupplierListQueryKey>,
        string | undefined
    >({
        queryKey: insightSupplierListQueryKey(bagId ?? 0, debouncedSearch),
        queryFn: async ({pageParam, signal}) => {
            if (pageParam) {
                const {data} = await queryHttp.get<InsightSupplierListResponse>(
                    pageParam,
                    {signal},
                );
                return data;
            }
            const {data} = await queryHttp.get<InsightSupplierListResponse>(
                `/data/${bagId}/supplier_parent_supplier/`,
                {
                    params: {search: debouncedSearch || undefined},
                    signal,
                },
            );
            return data;
        },
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.next || undefined,
        enabled: Boolean(bagId),
        staleTime: 30_000,
    });
}
